import { useState } from 'react';
import { AMOUNT_BASIS_LABELS } from '../utils/amountBasis';
import type { AmountBasis } from '../utils/amountBasis';
import type { SelectionUnit } from '../utils/invoiceSelection';
import type { ServiceSelectionRow } from '../utils/selectionTableRows';
import { SERVICE_BUTTON_GRID, SERVICE_DUE_CELL, SERVICE_NET_CELL, SERVICE_ROW_GRID, SoldServiceRow } from './SoldServiceRow';

interface Props {
    rows: ServiceSelectionRow[];
    emptyMessage: string;
    flexible: boolean;
    selectedKeys: ReadonlySet<string>;
    onToggle: (unit: SelectionUnit) => void;
    /** Disables every select checkbox while a submission is in flight; expansion still works. */
    locked?: boolean;
    summary?: string;
    basis?: AmountBasis;
    readOnlyItems?: boolean;
}

/** One expanded service at a time; expanding another service replaces it rather than stacking. */
export function SoldServicesTable({ rows, emptyMessage, flexible, selectedKeys, onToggle, locked = false, summary, basis = 'net', readOnlyItems = false }: Props) {
    const [expandedId, setExpandedId] = useState<string | null>(null);
    const toggleExpand = (id: string) => setExpandedId((current) => (current === id ? null : id));

    return (
        <section aria-labelledby="sold-services-heading" className="space-y-2">
            <h3 id="sold-services-heading" className="text-sm font-semibold text-mp-foreground">
                Services
            </h3>
            {summary && <p className="text-sm text-mp-muted @min-[58rem]:hidden">{summary}</p>}
            {rows.length === 0 ? (
                <p className="text-sm text-mp-muted">{emptyMessage}</p>
            ) : (
                <div className="@container space-y-2">
                    <div aria-hidden="true" className={`${SERVICE_ROW_GRID} text-xs font-semibold uppercase tracking-wide text-mp-muted`}>
                        <span />
                        <span className={`${SERVICE_BUTTON_GRID} pr-3`}>
                            <span />
                            <span className="col-start-2 row-start-1">Name</span>
                            <span className={`${SERVICE_DUE_CELL} hidden @min-[28rem]:block`}>Due</span>
                            <span className={`${SERVICE_NET_CELL} text-right`}>{AMOUNT_BASIS_LABELS[basis].column}</span>
                        </span>
                    </div>
                    <ul className="divide-y divide-mp-border rounded border border-mp-border">
                        {rows.map((row) => (
                            <SoldServiceRow
                                key={row.service.id}
                                row={row}
                                expanded={expandedId === row.service.id}
                                onToggleExpand={toggleExpand}
                                flexible={flexible}
                                selectedKeys={selectedKeys}
                                onToggle={onToggle}
                                locked={locked}
                                basis={basis}
                                readOnlyItems={readOnlyItems}
                            />
                        ))}
                    </ul>
                </div>
            )}
        </section>
    );
}
